// Active-locale singleton. Resolves the locale from the URL at import time
// (BEFORE React mounts), loads the matching dictionary, sets <html lang>, and
// exposes locale-aware helpers.
//
// Import this for its side effect as the FIRST import in every src/*.jsx entry:
//   import '../scripts/i18n/bootstrap.js';
//
// The dictionaries are imported statically so lookups are synchronous (no
// flash of untranslated content). Switching language is a full-page navigation,
// so only the active dictionary is ever consulted at runtime.
//
// Only ENABLED_LOCALES are bundled. es/de/fr have no built pages (the i18n
// build emits clones for enabled locales only), so shipping their ~70 KB of
// JSON to every visitor bought nothing. To re-enable one, import it here AND
// add it to ENABLED_LOCALES in index.js.

import { DEFAULT_LOCALE, getLocaleFromPath, applyBasePath, LOCALE_HREFLANG, LOCALE_TO_PREFIX } from './index.js';

import enGB from './en-GB.json';
import enUS from './en-US.json';
import ptBR from './pt-BR.json';

const DICTS = { 'en-GB': enGB, 'en-US': enUS, 'pt-BR': ptBR };

const resolved = (typeof window !== 'undefined')
  ? getLocaleFromPath(window.location.pathname)
  : { locale: DEFAULT_LOCALE, prefix: '', basePath: '' };

// `let`, not `const`: the build-time prerender (scripts/prerender.mjs) renders
// the same module graph once per locale in a single Node process, so it needs
// to retarget this singleton between renders via setActiveLocale(). In the
// browser nothing calls that setter — the locale stays whatever the URL
// resolved to at import time, exactly as before.
let activeLocale = resolved.locale;
let activeBasePath = resolved.basePath;

if (typeof document !== 'undefined') {
  try { document.documentElement.lang = LOCALE_HREFLANG[activeLocale] || activeLocale; } catch (_) { /* ignore */ }
}

// Prerender-only: point the singleton at another locale before rendering.
// No-op guard for the browser, where the URL is the only source of truth.
export function setActiveLocale(locale) {
  if (typeof window !== 'undefined') return;
  activeLocale = DICTS[locale] ? locale : DEFAULT_LOCALE;
  activeBasePath = LOCALE_TO_PREFIX[activeLocale] ? '/' + LOCALE_TO_PREFIX[activeLocale] : '';
}

export function getActiveLocale() { return activeLocale; }
export function getBasePath() { return activeBasePath; }
export function getDict() { return DICTS[activeLocale] || DICTS[DEFAULT_LOCALE]; }
export function getFallbackDict() { return DICTS[DEFAULT_LOCALE]; }

// Localize an internal app path against the active locale (no-op at the root).
export function localizedHref(path) { return applyBasePath(path, activeBasePath); }

// Persist the user's explicit language choice so the edge middleware respects it
// on subsequent visits instead of re-applying geo detection.
export function setLocaleCookie(locale) {
  if (typeof document === 'undefined') return;
  try {
    document.cookie = `sw_locale=${encodeURIComponent(locale)}; path=/; max-age=31536000; samesite=Lax`;
  } catch (_) { /* ignore */ }
}
